/**
 * Mirrors the backend's Pydantic models. Dates arrive as ISO `YYYY-MM-DD` strings and
 * are kept that way: every use here is display or keying, and parsing them into `Date`
 * would silently shift them into the viewer's timezone.
 */

export type Direction = 'increase' | 'decrease' | 'stable'
export type QualityStatus = 'pass' | 'partial' | 'empty'

export interface DateWindow {
  current_start: string
  current_end: string
  comparison_start: string
  comparison_end: string
}

export type PresetName = 'editorial' | 'editorial_commerce' | 'ecommerce'

export interface ReportPreset {
  name: PresetName
  ga4_metrics: string[]
  kpis: string[]
  series_metrics: string[]
  findings_metric: string
}

export interface ReportingProfile {
  name: string
  window: DateWindow
  preset: ReportPreset
  external_label: string
  minimum_segment_users: number
  minimum_segment_share_percent: number
  anomaly_threshold_percent: number
  max_findings: number
}

export interface KPIResult {
  name: string
  current: number
  previous: number
  change_percent: number
}

export interface SeriesPoint {
  date: string
  value: number
  comparison_date: string
  comparison_value: number
  /**
   * A day GA4 has not finished counting. The value is a **floor** that only rises, so
   * it must never be read as a decline. Its baseline is a *whole* day, so the gap
   * between the two closes as the day fills in.
   */
  provisional?: boolean
  /** False when no baseline was fetched for `comparison_date` — not a measured zero. */
  comparison_available?: boolean
}

export interface MetricSeries {
  metric: string
  points: SeriesPoint[]
}

export interface Finding {
  metric: string
  segment: string
  current: number
  previous: number
  change_percent: number
  direction: Direction
}

export interface DataQuality {
  status: QualityStatus
  aggregate_rows_only: true
  raw_rows_processed: 0
  days_expected: number
  days_returned: number
  notes: string[]
}

export interface AnalysisResult {
  profile: ReportingProfile
  kpis: KPIResult[]
  series: MetricSeries[]
  findings: Finding[]
  quality: DataQuality
  analyzed_rows: number
}

export interface PropertySummary {
  property_id: string
  display_name: string
  preset: string
  /** Whether to offer the Paywall tab. Not a permission — the endpoint enforces it. */
  has_paywall: boolean
}

export interface PropertiesResponse {
  properties: PropertySummary[]
  default_report_date: string
}

/**
 * The current session. `auth_enabled` is false on a deployment with no accounts
 * configured, where everyone is served and there is nothing to sign in to.
 */
export interface SessionInfo {
  authenticated: boolean
  auth_enabled: boolean
  /** True when nobody has an account yet: show first-run setup, not a login form. */
  needs_setup: boolean
  is_admin: boolean
  /** May change tracked events, funnels and watches. The API enforces it too. */
  can_configure: boolean
  username: string
  display_name: string
  property_count: number
}

// --- administration ---

export interface UserSummary {
  username: string
  display_name: string
  admin: boolean
  /** Property ids, or `["*"]` for all of them including ones added later. */
  properties: string[]
  /** May change tracked events, funnels and watches. Always true for an admin. */
  can_configure: boolean
}

export interface NewUser {
  username: string
  password: string
  display_name: string
  admin: boolean
  properties: string[]
  can_configure: boolean
}

export interface UserPatch {
  password?: string
  display_name?: string
  admin?: boolean
  properties?: string[]
  can_configure?: boolean
}

export interface AgentSummary {
  name: string
  properties: string[]
}

/** Returned once, at creation. The server only stores a digest. */
export interface AgentCreated {
  name: string
  token: string
}

export interface PropertyRecord {
  property_id: string
  display_name: string
  preset: string
  has_paywall: boolean
  site_label: string
  credential_file: string
  /** Telegram chat id this property's alerts are delivered to. Empty means unrouted. */
  alert_chat_id: string
}

export interface TestAlertResult {
  property_id: string
  chat_id: string
  sent: boolean
}

export type CoverageState =
  | 'carrying'
  | 'dropped'
  | 'barely_arriving'
  | 'not_set_only'
  | 'no_events'
  | 'no_data'

/** How completely one event carried one parameter. Contains no parameter values. */
export interface ParameterCoverage {
  event_name: string
  parameter: string
  state: CoverageState
  covered: number
  not_set: number
  coverage_percent: number | null
  previous_coverage_percent: number | null
  /** How many different values arrived — a count, never the values. */
  distinct_values: number
  detail: string
}

export interface CredentialSummary {
  filename: string
  /** The address a client adds as a Viewer in GA4. Not a secret — it is the point. */
  client_email: string
  project_id: string
  size_bytes: number
  modified: string
  /** False when the file is present but does not parse as a service-account key. */
  valid: boolean
  problem: string
}

export interface PropertyAdminView {
  properties: PropertyRecord[]
  /** Service-account filenames present in data/secrets. Names only, never contents. */
  credentials: string[]
  /** The same files described — address, project, validity. Never key material. */
  credential_details: CredentialSummary[]
  /** A property id set in the environment that properties.json does not list, and is
   *  therefore no longer served. Null when there is nothing to report. */
  shadowed_environment_property: string | null
}

/** Outcome of a live GA4 call testing one property's credential. */
export type AccessState = 'ok' | 'no_credential' | 'access_denied' | 'bad_credential' | 'unavailable'

export interface AccessCheck {
  property_id: string
  state: AccessState
  detail: string
}

// --- watches and the daily check ---

export type WatchKind = 'metric' | 'event'
export type Severity = 'info' | 'warning' | 'critical'
export type WatchStatus = 'ok' | 'attention' | 'no_data' | 'muted'

export const WATCHABLE_METRICS = [
  'users',
  'sessions',
  'page_views',
  'engaged_sessions',
  'engagement_rate',
  'sessions_per_user',
  'transactions',
  'revenue',
] as const

export interface Watch {
  id: string
  label: string
  kind: WatchKind
  target: string
  segment: string | null
  drop_percent: number | null
  spike_percent: number | null
  minimum: number | null
  severity: Severity
  enabled: boolean
  muted_until: string | null
}

export interface WatchResult {
  id: string
  label: string
  status: WatchStatus
  severity: Severity
  current: number
  previous: number
  change_percent: number
  triggered: string[]
  reason: string
  /**
   * The rolling 7-day average and the movement against it. Present only for
   * unsegmented metric watches, which are the ones judged against two baselines;
   * null means the watch was decided on the headline baseline alone.
   */
  rolling_previous?: number | null
  rolling_change_percent?: number | null
}

export interface DailyCheck {
  status: 'ok' | 'attention' | 'no_data'
  summary: string
  site: string
  report_date: string
  comparison_date: string
  checks_total: number
  checks_attention: number
  attention: WatchResult[]
  ok: WatchResult[]
  skipped: WatchResult[]
  privacy: Record<string, boolean | number>
}

export interface MetricVolatility {
  metric: string
  samples: number
  median_change_percent: number
  p75_change_percent: number
  p90_change_percent: number
  max_change_percent: number
  suggested_drop_percent: number | null
  suggested_spike_percent: number | null
  expected_alerts_per_week: number | null
  note: string
}

export interface VolatilitySuggestion {
  days_analysed: number
  metrics: MetricVolatility[]
}

// --- the evidence package (what hermes receives) ---

export type BaselineKind =
  | 'previous_day'
  | 'same_weekday_last_week'
  | 'last_year'
  | 'rolling_7day'

export interface Comparison {
  baseline: BaselineKind
  baseline_label: string
  baseline_date: string | null
  current: number
  previous: number
  change_percent: number
  absolute_change: number
  available: boolean
}

export interface SegmentContribution {
  dimension: string
  segment: string
  metric: string
  current: number
  previous: number
  change_percent: number
  absolute_change: number
  contribution_percent: number
  direction: 'increase' | 'decrease' | 'stable'
  share_of_current: number
  supporting_context: Record<string, number>
}

export interface DimensionBreakdown {
  dimension: string
  metric: string
  total_change: number
  approximate: boolean
  note: string
  contributors: SegmentContribution[]
}

export type Significance = 'major' | 'notable' | 'minor'

export interface MetricFinding {
  metric: string
  label: string
  significance: Significance
  current: number
  headline: Comparison
  supporting: Comparison[]
  breakdowns: DimensionBreakdown[]
  baseline_conflict: string
}

export interface EventFinding {
  event_name: string
  label: string
  metric: string
  significance: Significance | null
  current: number
  headline: Comparison
  supporting: Comparison[]
  breakdowns: DimensionBreakdown[]
  baseline_conflict: string
}

export interface FunnelRate {
  label: string
  from_event: string
  to_event: string
  from_sessions: number
  to_sessions: number
  rate_percent: number
  previous_rate_percent: number
  rate_change_percent: number
  baseline_label: string
}

/** One step of an ordered funnel, counted in sessions containing the event. */
export interface FunnelStepResult {
  event_name: string
  label: string
  position: number
  sessions: number
  previous_sessions: number
  sessions_change_percent: number
  /** Conversion from the step before. 100 at position 0, by definition. */
  step_rate_percent: number
  previous_step_rate_percent: number
  step_rate_change_percent: number
  /** Conversion from the funnel's entry step. */
  overall_rate_percent: number
  drop_off_sessions: number
  drop_off_percent: number
}

export interface FunnelSegment {
  segment: string
  share_of_entries_percent: number
  entries: number
  steps: FunnelStepResult[]
  biggest_drop_label: string
  biggest_drop_percent: number
}

export interface FunnelDimensionResult {
  dimension: string
  label: string
  /** A dimension is not collected on every step; this names the ones it covers. */
  covers_steps: string[]
  note: string
  /** Values withheld by the small-segment floor: "too few people", not "nobody". */
  suppressed_segments: number
  segments: FunnelSegment[]
  /**
   * The same steps with no split, from the dimensionless query — never the sum of
   * `segments`, which double-counts a session that touched two values. Restricted and
   * renumbered to `covers_steps`. `share_of_entries_percent` is zero because a share of
   * the total out of the total is not a reading; the table draws it as a dash.
   */
  total: FunnelSegment | null
}

export interface FunnelResult {
  name: string
  label: string
  baseline_label: string
  baseline_date: string | null
  status: 'ok' | 'no_data'
  steps: FunnelStepResult[]
  breakdowns: FunnelDimensionResult[]
  notes: string[]
}

export interface FunnelStep {
  event_name: string
  label: string
}

export interface FunnelBreakdown {
  key: string
  ga4_dimension: string
  label: string
  /** Empty means every step. */
  applies_to: string[]
  enabled: boolean
}

export interface FunnelDefinition {
  name: string
  label: string
  steps: FunnelStep[]
  breakdowns: FunnelBreakdown[]
  enabled: boolean
}

/* ---------- paywall ---------- */

/**
 * One action taken on a paywall, measured against the impressions that offered it.
 *
 * Subscribe and log-in are *parallel* outcomes of one impression, not steps in a path,
 * so both rates are divided out of views and neither is measured against the other.
 */
export interface PaywallAction {
  event_name: string
  label: string
  sessions: number
  previous_sessions: number
  sessions_change_percent: number
  /** Sessions taking this action per 100 that saw the paywall, over the whole window. */
  rate_percent: number
  previous_rate_percent: number
  rate_change_percent: number
}

/** One paywall id — or, on the totals row, the property as a whole. */
export interface PaywallVariant {
  paywall_id: string
  views: number
  previous_views: number
  views_change_percent: number
  /** Share of the split's impressions, not of the total: GA4 de-duplicates per value. */
  share_of_views_percent: number
  actions: PaywallAction[]
  /**
   * Units purchased, attributed through the item category the site tags each sold item
   * with (`paywall-<id>`). A different measure from the actions — those are sessions
   * containing a click, this is items sold — so it reads as a count, not a rate.
   */
  purchases: number
  previous_purchases: number
  purchases_change_percent: number
}

/** One action inside a single day/paywall cell of the matrix. */
export interface PaywallMatrixAction {
  event_name: string
  label: string
  sessions: number
  /** Sessions taking this action per 100 that saw this paywall on this day. */
  rate_percent: number
}

/**
 * One paywall on one day. `suppressed` is not zero: it is a cell below the reporting
 * floor, withheld with its figures left at zero, and it renders as a dash.
 */
export interface PaywallMatrixCell {
  date: string
  views: number
  /** Share of that day's split impressions, not of the window or the headline total. */
  share_of_views_percent: number
  actions: PaywallMatrixAction[]
  /**
   * Checkout steps reached from this paywall, attributed through the entry point the
   * journey began at (`paywall-<id>`) rather than through `paywall_id` — those events do
   * not carry one. Rated against this paywall's impressions like the actions.
   */
  journey: PaywallMatrixAction[]
  purchases: number
  suppressed: boolean
}

export interface PaywallMatrixRow {
  paywall_id: string
  cell: PaywallMatrixCell
}

/** One day of the window, with every reported paywall nested under it. */
export interface PaywallMatrixDay {
  date: string
  /** The day's split impressions — the denominator of the shares, not a headline total. */
  split_views: number
  rows: PaywallMatrixRow[]
  /**
   * The day's real figures, from the query with no breakdown dimension. Deliberately not
   * the sum of `rows` and it will not equal it: a session shown two paywalls is counted
   * once under each id, so `split_views` above overstates. `journey` is empty and
   * `share_of_views_percent` zero because neither has a dimensionless meaning.
   */
  total: PaywallMatrixCell | null
  /**
   * Purchases counted in `total` that appear on no visible row — a sale on a paywall the
   * floor withheld, or an unattributed one folded onto a `(not set)` row that was itself
   * withheld. Purchases are the one column that should add up, so the gap is stated
   * rather than left to look like an error.
   */
  withheld_purchases: number
  /**
   * Sales whose item category names no paywall. Carried on the day rather than only
   * inside the `(not set)` row, because that row is withheld on impressions — an
   * unrelated measurement that was taking the sale down with it.
   */
  unattributed_purchases: number
}

/**
 * The split re-cut by day: paywall ids down, dates across. A re-slice of the rows the
 * variant table already uses, so GA4's per-value de-duplication applies twice over and
 * neither a row nor a column sums to anything else on the page.
 */
export interface PaywallMatrix {
  dates: string[]
  /** Newest day first, each carrying its own rows ranked largest first. */
  days: PaywallMatrixDay[]
  /** Cells withheld by the per-day floor — a quiet day inside a variant that cleared it. */
  suppressed_cells: number
  /**
   * Journeys were recorded and not one named a paywall. The `entry_point` parameter only
   * began naming individual paywalls on 26 August 2026 and GA4 never backfills a custom
   * dimension, so an older window has every journey in `(not set)`. Drawn as a warning
   * rather than as a column of zeros: "nobody reached checkout from a paywall" and "the
   * parameter did not say which" are opposite claims.
   */
  journey_unattributed_only: boolean
  /** Checkout sessions whose entry point named no paywall — the header, a newsletter. */
  unattributed_journey_sessions: number
  minimum_segment_users: number
}

export interface PaywallReport {
  window: DateWindow
  status: 'ok' | 'no_data'
  /** From the query with no breakdown dimension. Never summed from `variants`. */
  total: PaywallVariant
  variants: PaywallVariant[]
  /** Variants withheld by the small-segment floor: "too few people", not "nobody". */
  suppressed_variants: number
  /** The same variants cut by day. Null when there is no split to cut. */
  matrix: PaywallMatrix | null
  minimum_segment_users: number
  series: MetricSeries[]
  /** Still-settling days the trend runs past the window. Zero for an older window. */
  provisional_days: number
  /** The most recent day GA4 returned anything for — its answer, not our clock. */
  frontier: string | null
  notes: string[]
  analyzed_rows: number
}

/**
 * Whether `customEvent:paywall_id` is carrying values yet.
 *
 * Read from the hours GA4 has already processed rather than from settled days, because
 * a custom dimension carries values only from the moment it is registered and GA4 never
 * backfills one — so immediately after registering, every settled day answers
 * `(not set)` whether the setup is right or wrong.
 */
export type PaywallCheckState = 'carrying' | 'not_set_only' | 'no_impressions' | 'no_data'

export interface PaywallValueSighting {
  paywall_id: string
  /** Event-counted, like the pulse: "how much arrived", not "how many people". */
  events: number
  last_seen: string | null
}

export interface PaywallDimensionCheck {
  state: PaywallCheckState
  detail: string
  /** GA4's own data frontier, in the property's timezone. Never the reader's clock. */
  frontier: string | null
  values: PaywallValueSighting[]
  not_set_events: number
  days_examined: number
}

export interface DataQualityNote {
  name: string
  status: string
  detail: string
}

export interface EvidencePackage {
  site: string
  report_date: string
  generated_for: string
  contract: string
  data_quality_status: string
  safe_to_interpret: boolean
  data_quality: DataQualityNote[]
  findings: MetricFinding[]
  events: EventFinding[]
  funnels: FunnelRate[]
  funnel_reports: FunnelResult[]
  suggested_follow_ups: string[]
  privacy: Record<string, unknown>
  audit_lines: string[]
  ga4_tokens_used: number
  generated_at: string | null
}

export interface TrackedEvent {
  event_name: string
  label: string
  rate_against: string | null
  rate_label: string
  enabled: boolean
  /**
   * Consecutive empty hours before the pulse calls this event silent. Null takes the
   * shared default. Detection is roughly GA4's processing lag plus this window, so a
   * high-volume event is worth tightening — and a genuinely intermittent one loosening.
   */
  silent_after_hours: number | null
  /**
   * Event-scoped custom dimensions this event should carry, by GA4 parameter name.
   * Monitored for coverage only — whether occurrences arrive with a value, never which.
   */
  parameters: string[]
  /** How much the same hours a week ago must have carried for silence to mean anything. */
  minimum_expected: number | null
}

export interface ConnectionStatus {
  configured: boolean
  property_id: string
  credential_file: string
  credential_file_readable: boolean
}

/* ---------- daybook ---------- */

export interface DayMetric {
  metric: string
  current: number
  previous: number
  change_percent: number
}

export interface DayMovement {
  segment: string
  metric: string
  current: number
  previous: number
  change_percent: number
}

export interface DayFunnelRate {
  name: string
  label: string
  overall_rate_percent: number
  previous_rate_percent: number
}

/**
 * One entry in the daybook.
 *
 * Flat on purpose: a row must render without loading the briefing it came from, or
 * the stream would cost as much as the dashboard it replaced.
 */
export interface DaySummary {
  report_date: string
  status: WatchStatusVerdict
  summary: string
  comparison_date: string | null
  safe_to_interpret: boolean
  data_quality_status: string
  checks_total: number
  checks_attention: number
  attention: string[]
  metrics: DayMetric[]
  movements: DayMovement[]
  funnels: DayFunnelRate[]
  recorded_at: string | null
}

export type WatchStatusVerdict = 'ok' | 'attention' | 'no_data'

/**
 * One tracked event's pulse, read from hours GA4 has not finished counting.
 *
 * `firing` is a fact — counts only ever grow as a day settles. `silent` is a suspicion
 * strong enough to act on, raised only when the property itself had traffic in those
 * hours. `quiet` means the same hours a week ago carried too little for absence to mean
 * anything, and `never_seen` means the event has no history at all, which is not the
 * same as having stopped.
 */
export type LivenessStatus = 'firing' | 'silent' | 'quiet' | 'never_seen'

export interface HourPoint {
  hour: string
  value: number
}

export interface EventLiveness {
  event_name: string
  label: string
  status: LivenessStatus
  /** The thresholds actually applied, after any per-event override. */
  watch_hours: number
  minimum_expected: number
  /**
   * The longest run of empty hours between two occurrences, excluding any gap still
   * open. This is what `silent_after_hours` should be set above.
   */
  longest_normal_gap: number | null
  /**
   * A window measured off this event's own rhythm, with headroom, excluding hours it was
   * known to be broken. Suggested and never applied: a threshold that moved on its own
   * would learn from every outage it failed to catch.
   */
  suggested_silent_after_hours: number | null
  /** How often the window in force would fire on ordinary quiet, per week. */
  false_alerts_per_week: number | null
  /** The window sits at or below the event's own normal gap — the cry-wolf condition. */
  window_below_normal_gap: boolean
  /** Counted events, not sessions: the question is whether anything fired at all. */
  events_last_24h: number
  last_seen: string | null
  /** Measured against GA4's own data frontier, never against the reader's clock. */
  hours_since_last_seen: number | null
  observed: number
  expected: number
  hourly: HourPoint[]
}

export interface LivenessReport {
  /** The most recent hour the property recorded any traffic, in its reporting timezone. */
  frontier: string | null
  /**
   * The newest hour complete enough to judge an absence against — behind `frontier` by
   * however long GA4's processing tail is running. Silence is judged to here; the chart
   * and `last_seen` still run to the frontier.
   */
  settled_frontier: string | null
  /** GA4 is behind; nothing here can tell an outage from a delay, so nothing is claimed. */
  data_is_stale: boolean
  watch_hours: number
  minimum_expected: number
  events: EventLiveness[]
}

export interface DaybookResponse {
  days: DaySummary[]
  /** How many days behind the leading one came off disk — the offer to backfill. */
  recorded: number
  requested: number
}

export interface BackfillResult {
  built: number
  failed: number
  requested: number
}

export interface FunnelHistoryPoint {
  date: string
  overall_rate_percent: number
}

export interface FunnelReportResponse {
  funnel: FunnelResult
  report_date: string
  history: FunnelHistoryPoint[]
}

export interface QuotaBucket {
  name: string
  remaining: number
  /** What the most recent request cost this bucket — not the window's total. */
  last_request: number
}

export interface DailyTokenUsage {
  /** Spend since the highest balance seen. A floor until `since_reset` is true. */
  used: number
  remaining: number
  /** Known only once a reset has been observed; null before that. */
  limit: number | null
  since_reset: boolean
}

export interface QuotaReport {
  daily: DailyTokenUsage
  buckets: QuotaBucket[]
  tokens_used_this_process: number
  last_request_tokens: number
}
